import { memo, useCallback, useRef } from "react";
import { useRouteLoaderData } from "@remix-run/react";

import { writeToClipboard } from "~/utils/clipboard";
import { formatTimestamp } from "~/utils/helpers";
import { track } from "~/utils/track";
import { useLabels } from "~/hooks/useLabels";
import { useToasts } from "~/components/toast/useToasts";
import { Transcript } from "~/components/transcript/Transcript";
import { Button } from "~/components/button/Button";
import { Text } from "~/components/text/Text";

import type {
  TranscriptRef,
  TranscriptState,
} from "~/components/transcript/Transcript";
import type { SessionRouteLoaderData } from "./route";

export interface SessionTranscriptProps {
  currentTime: number;
  onRequestClose?: () => void;
  onRequestSeek?: (timestamp: number) => void;
  isDesktopLayout?: boolean;
}

export const SessionTranscript = memo(function SessionTranscript({
  currentTime,
  onRequestClose,
  onRequestSeek,
  isDesktopLayout,
}: SessionTranscriptProps) {
  const { t } = useLabels();

  const { showToast } = useToasts();

  const data = useRouteLoaderData(
    "routes/$class_.sessions.$session",
  ) as SessionRouteLoaderData;

  const transcriptRef = useRef<TranscriptRef>(null);

  const handleCloseClick = useCallback(() => {
    transcriptRef.current?.closeActionsModal();
    onRequestClose?.();
  }, [onRequestClose]);

  const actions = useCallback(
    ({ selectedTimestamps, selectedText }: TranscriptState) => [
      {
        icon: "clone",
        text: t("transcript.cta.copy"),
        onClick() {
          writeToClipboard(selectedText)
            .then(() => {
              showToast({
                title: t("transcript.copyToast.success"),
                intent: "success",
                icon: "check",
                dismissable: false,
              });

              track("crTranscriptCopy", {
                className: data.session.class?.slug,
                sessionNumber: data.session.position,
                timestamp: currentTime,
              });
            })
            .catch(() => {
              showToast({
                title: t("transcript.copyToast.error"),
                intent: "error",
              });
            })
            .finally(() => {
              transcriptRef.current?.closeActionsModal();
            });
        },
      },
      {
        icon: "play",
        text: t("transcript.cta.play", {
          time: formatTimestamp(selectedTimestamps[0]),
        }),
        onClick() {
          onRequestSeek?.(selectedTimestamps[0]);
          transcriptRef.current?.closeActionsModal();
        },
      },
    ],
    [
      t,
      showToast,
      data.session.class?.slug,
      data.session.position,
      currentTime,
      onRequestSeek,
    ],
  );

  if (!data.transcript) return null;

  if (!isDesktopLayout) {
    return (
      <Transcript
        actions={actions}
        currentTimestamp={currentTime}
        data={data.transcript}
        postTitle={t("transcript.modal.actions.postTitle", {
          number: data.session.position,
          title: data.session.title,
        })}
        ref={transcriptRef}
        isDesktopLayout={isDesktopLayout}
      />
    );
  }

  return (
    <div className="SessionTranscript">
      <div className="SessionTranscript__header">
        <Text size="lg" weight="bold">
          {t("common.transcript")}
        </Text>

        <Button
          appearance="control"
          icon="xmark"
          label={t("common.cta.close")}
          shape="circle"
          size="sm"
          onClick={handleCloseClick}
        />
      </div>

      <Transcript
        actions={actions}
        currentTimestamp={currentTime}
        data={data.transcript}
        postTitle={t("transcript.modal.actions.postTitle", {
          number: data.session.position,
          title: data.session.title,
        })}
        ref={transcriptRef}
        isDesktopLayout={isDesktopLayout}
      />
    </div>
  );
});
